/** Vertrags-Hooks der Pflege-App (eigener /api/pflege/vertraege-Scope). */
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch, apiPost } from "@vimaana/ui";

const BASE = "/api/pflege/vertraege";

export type VertragSummary = {
  id: string;
  nummer: string;
  kunde_id: string;
  kunde_name: string;
  status: string;
  beginn: string | null;
  zahlweise: string;
  faktor: string;
  jahres_netto: string | null;
  monats_netto: string | null;
  positionen: number;
  /** Letzte echte Zustimmung (ohne Probelauf) — für „Neu abgeschlossen" unter Aufgaben. */
  zugestimmt_am?: string | null;
};

export type Kandidat = {
  id: string;
  name: string;
  ort: string;
  pflege_monate_json: string | null;
  hat_vertrag: boolean;
};

export function useVertraege() {
  return useQuery<VertragSummary[]>({
    queryKey: ["pflege", "vertraege"],
    queryFn: () => apiGet<VertragSummary[]>(BASE),
    staleTime: 15_000,
  });
}

export function useKandidaten() {
  return useQuery<Kandidat[]>({
    queryKey: ["pflege", "kandidaten"],
    queryFn: () => apiGet<Kandidat[]>(`${BASE}/kandidaten`),
    staleTime: 60_000,
  });
}

export const VERTRAG_STATUS_LABEL: Record<string, string> = {
  entwurf: "Entwurf", angeboten: "Angeboten", zugestimmt: "Zugestimmt",
  aktiv: "Aktiv", pausiert: "Pausiert", gekuendigt: "Gekündigt", beendet: "Beendet",
};

export function eur(n: number | string | null): string {
  if (n === null || n === "") return "—";
  // Ganze Beträge ohne Cent, alles andere mit: bis zum 28.09.2026 stand hier
  // immer „ohne Cent", und aus 4,50 € je Laufmeter wurden „5 €".
  const wert = Number(n);
  const ganz = Math.abs(wert - Math.round(wert)) < 0.005;
  return wert.toLocaleString("de-DE", {
    style: "currency", currency: "EUR",
    minimumFractionDigits: ganz ? 0 : 2, maximumFractionDigits: ganz ? 0 : 2,
  });
}

// ─────────────────────────── Vertrags-Detail (W7) ──────────────────────────

/** Wie der Server eine Vertragsposition liefert — Zahlen als Text (numeric).
 *  Vollstaendig, damit ein wieder aufgenommener Entwurf nichts verliert:
 *  Kosten, Zeit und Lohnanteil sind Schnappschuesse aus dem Katalog und
 *  koennen nicht aus dem Katalog nachgeladen werden (der darf sich aendern). */
export type VertragLeistung = {
  id: string;
  leistung_id: string | null;
  titel_override: string | null;
  katalog_titel?: string | null;
  menge: string;
  einheit: string;
  einzelpreis_netto: string;
  kosten_snapshot: string;
  zeit_min_snapshot: string;
  lohnanteil_prozent: string;
  intervall: string;
  // MIT Mehrfachnennung: [3,3,5] heißt zweimal im März. `anzahl_pro_jahr`
  // liefert der Server seit Migration 152 nicht mehr — die Zahl kommt aus
  // `einsaetze()` in lib/frequenz.
  monate: number[];
  optional: boolean;
  beauftragt: boolean;
  material_notiz: string;
  sort_order: number;
};

export type Kalkulation = {
  jahres_netto: string;
  monats_netto: string;
  jahres_kosten: string;
  jahres_zeit_stunden: string;
  deckungsbeitrag: string;
  db_prozent: string | null;
  db_pro_stunde: string | null;
  lohnanteil_netto_35a: string;
  faktor: string;
};

export type Ausfertigung = {
  erstellt_am: string;
  groesse: number;
  sha256: string;
  sha_kurz: string;
  dateiname: string;
  vorlage_version: number;
  anlass: string;
  nextcloud_pfad: string;
  kundenlink: string;
  /** Die Blätter des Satzes (Urkunde, Anlage 1 mit dem Leistungsverzeichnis, …). */
  teile?: { teil: string; titel: string; anzeige?: string; groesse: number }[];
};

export type VertragPruefung = {
  ok: boolean;
  /** Sperren — ohne sie gibt es keine Vertragsnummer und keinen Link. */
  fehler: string[];
  /** Hinweise — sie halten nichts auf, aber sie stehen sonst später im Streit. */
  warnungen: string[];
};

export type VertragDetail = VertragSummary & {
  /** 'verbraucher' | 'unternehmer' — steuert Laufzeitgrenzen, Widerruf,
   *  Verzugssatz und Preisdarstellung im Vertrag. */
  vertragsart: string;
  /** Die festgeschriebene Ausfertigung. Steht hier etwas, liefert jeder Abruf
   *  genau diese Datei — der Vertrag ist ab dann unveränderlich. */
  ausfertigung: Ausfertigung | null;
  laufzeit_monate: number;
  verlaengerung_monate: number;
  kuendigungsfrist_monate: number;
  /** Gesetzt, sobald eine Kündigung bestätigt wurde — steuert, ob die
   *  Schlussabrechnung überhaupt angeboten wird. */
  gekuendigt_am: string | null;
  /** Gesetzt, sobald eine Preisanpassung angekündigt wurde (§ 8). */
  preisanpassung_zum: string | null;
  gekuendigt_zum: string | null;
  ende: string | null;
  zahlungsart: string;
  sondervereinbarungen: string;
  version: number;
  projekt_id: string | null;
  termine_generiert: number;
  leistungen: VertragLeistung[];
  kalkulation: Kalkulation;
  /** Was der Kunde im ersten Vertragsjahr zahlt — brutto, aus `vertrag_raten.erstes_jahr`
   *  (dieselbe Rechnung wie Kundenseite, Urkunde und Lastschrift). */
  erstes_jahr?: {
    jahr: number; volljahr: boolean; anzahl: number;
    rate_brutto: string | null; summe_brutto: string; folgejahr_brutto: string;
    zeile: string; folgejahr_zeile: string;
  } | null;
  ist: {
    stunden: string; lohnkosten: string; ausgaben: string;
    umsatz_bezahlt: string; raten_bezahlt: number;
    einsaetze_gesamt: number; einsaetze_erledigt: number; gewinn_bisher: string;
  } | null;
  zustimmung: ZustimmungInfo | null;
};

export type ZustimmungInfo = {
  token: string;
  url: string;
  gesendet_am: string | null;
  gesendet_an?: string | null;
  geoeffnet_am: string | null;
  zugestimmt_am: string | null;
  abgelehnt_am: string | null;
  zustimmer_name: string | null;
  /** Bis wann das offene Angebot geöffnet werden kann (Mig 166); erneutes Senden verlängert. */
  gueltig_bis?: string | null;
  /** Wohin der Abschluss-Code ging: sms oder email. */
  code_kanal?: string | null;
  code_bestaetigt_am?: string | null;
};

export function useVertrag(id: string | undefined) {
  return useQuery<VertragDetail>({
    queryKey: ["pflege", "vertrag", id],
    queryFn: () => apiGet<VertragDetail>(`${BASE}/${id}`),
    enabled: !!id,
  });
}

/** Prüfliste vor dem Festschreiben — dieselbe, die der Server erzwingt.
 *  Bewusst kein `staleTime`: sie soll den Stand von jetzt zeigen. */
export function useVertragPruefung(id: string | undefined, aktiv = true) {
  return useQuery<VertragPruefung>({
    queryKey: ["pflege", "pruefung", id],
    queryFn: () => apiGet<VertragPruefung>(`${BASE}/${id}/pruefung`),
    enabled: !!id && aktiv,
  });
}

/** Ausfertigung von Hand festschreiben — für den Papierweg (unterschrieben
 *  zurückgeschickt statt online zugestimmt). Idempotent im Backend. */
export function useAusfertigungFestschreiben(id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<VertragDetail>(`${BASE}/${id}/ausfertigung`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pflege", "vertrag", id] });
      qc.invalidateQueries({ queryKey: ["pflege", "vertraege"] });
    },
  });
}

// ─────────────────────────── Fristen und Kündigungen (E1) ──────────────────

export type Fristen = {
  beginn: string | null;
  hinweis?: string;
  erstlaufzeit_ende?: string;
  letzter_kuendigungstermin_erstlaufzeit?: string;
  verlaengerung?: string;
  laeuft_unbefristet?: boolean;
  laufende_periode_ende?: string | null;
  naechster_kuendigungstermin?: string | null;
  kuendigung_heute_wirksam_zum?: string;
  tage_bis_kuendigungstermin?: number | null;
  widerruf_ablauf?: string | null;
  in_widerrufsfrist?: boolean;
  preisanpassung_fruehestens?: string;
  gekuendigt_zum?: string | null;
};

export type FristenEintrag = {
  id: string; nummer: string; kunde_name: string;
  jahres_netto: string | null; status: string; fristen: Fristen;
};

export type FristenUebersicht = {
  stichtag: string;
  offene_kuendigungen: number;
  laufen_aus: FristenEintrag[];
  in_widerrufsfrist: FristenEintrag[];
  preisanpassung_faellig: FristenEintrag[];
};

export type Kuendigung = {
  id: string;
  vertrag_id: string | null;
  nummer: string | null;
  kunde_name: string | null;
  vertrag_status: string | null;
  eingang_am: string;
  kanal: string;
  art: string;
  erklaerer_name: string;
  erklaerer_kontakt: string;
  kennung: string;
  wunsch_zum: string | null;
  wirksam_zum: string | null;
  bestaetigt_am: string | null;
  bemerkung: string;
  /** Was der Fristenrechner vorschlägt — mit Begründung im Klartext. */
  vorschlag: { wirksam_zum: string | null; begruendung: string };
};

export function useFristenUebersicht(tage = 90) {
  return useQuery<FristenUebersicht>({
    queryKey: ["pflege", "fristen", tage],
    queryFn: () => apiGet<FristenUebersicht>(`${BASE}/fristen?tage=${tage}`),
    staleTime: 60_000,
  });
}

export function useVertragFristen(id: string | undefined) {
  return useQuery<Fristen>({
    queryKey: ["pflege", "vertrag-fristen", id],
    queryFn: () => apiGet<Fristen>(`${BASE}/${id}/fristen`),
    enabled: !!id,
  });
}

export function useKuendigungen(offen = true) {
  return useQuery<Kuendigung[]>({
    queryKey: ["pflege", "kuendigungen", offen],
    queryFn: () => apiGet<Kuendigung[]>(`${BASE}/kuendigungen?offen=${offen}`),
    staleTime: 30_000,
  });
}

/** Nach jedem Schreiben an einer Kündigung ist alles veraltet, was Fristen
 *  zeigt — Übersicht, Liste und der betroffene Vertrag. */
function useKuendigungSchreiben<T>(fn: (v: T) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pflege", "kuendigungen"] });
      qc.invalidateQueries({ queryKey: ["pflege", "fristen"] });
      qc.invalidateQueries({ queryKey: ["pflege", "vertraege"] });
      qc.invalidateQueries({ queryKey: ["pflege", "vertrag"] });
    },
  });
}

export function useKuendigungZuordnen() {
  return useKuendigungSchreiben<{ kid: string; vertrag_id: string }>(
    ({ kid, vertrag_id }) => apiPost(`${BASE}/kuendigungen/${kid}/zuordnen`, { vertrag_id }),
  );
}

export function useKuendigungBestaetigen() {
  return useKuendigungSchreiben<{ kid: string; wirksam_zum?: string | null; bemerkung?: string }>(
    ({ kid, wirksam_zum, bemerkung }) =>
      apiPost<{ ok: boolean; wirksam_zum: string; begruendung: string }>(
        `${BASE}/kuendigungen/${kid}/bestaetigen`,
        { wirksam_zum: wirksam_zum || null, bemerkung: bemerkung || "" },
      ),
  );
}

// ─────────────────────── Mehraufwand und Schlussabrechnung (E2) ────────────

export type Mehraufwand = {
  id: string;
  vertrag_id: string;
  termin_id: string | null;
  datum: string;
  art: string;
  leistung_id: string | null;
  titel: string;
  menge: string;
  einheit: string;
  einzelpreis_netto: string;
  betrag_netto: string;
  notiz: string;
  /** Wie wurde beauftragt — § 6 verlangt ab 75 € netto Textform. */
  beauftragung: string;
  /** true, wenn der Posten über 75 € liegt und kein Beauftragungsvermerk da ist. */
  beauftragung_noetig: boolean;
  /** nicht_noetig | offen | erteilt | abgelehnt — Stand des Freigabe-Links. */
  freigabe_status: string;
  freigabe_am: string | null;
  abgerechnet_am: string | null;
  rechnung_nummer: string;
};

export type MehraufwandBody = {
  datum?: string; art?: string; termin_id?: string | null; leistung_id?: string | null;
  titel: string; menge: number; einheit: string; einzelpreis_netto: number;
  notiz?: string; beauftragung?: string;
};

export type Schlussabrechnung = {
  vertrag_id: string; nummer: string | null; kunde_name: string | null;
  status: string; ende: string | null;
  leistungen_netto: string;
  berechnet_netto: string;
  differenz_netto: string;
  /** So wird ausgeglichen: Verbraucher aus den Bruttoraten, sonst netto + USt. */
  differenz_brutto: string;
  leistungen_brutto?: string;
  berechnet_brutto?: string;
  /** Nur bei der Jahresabrechnung: das Abrechnungsjahr und ein Satz, wie der
   *  Ausgleich zum Kunden kommt (Einzug oder Überweisung). */
  jahr?: number | null;
  ausgleich?: string;
  /** Mehraufwand, der in die Abschlussrate eingerechnet ist (26.10.2026). */
  mehraufwand_netto?: string;
  mehraufwand_brutto?: string;
  mehraufwand_im_ausgleich?: Mehraufwand[];
  richtung: "nachzahlung" | "gutschrift" | "ausgeglichen";
  hinweis: string;
  zeilen: {
    leistung_id: string; titel: string; menge: string; einheit: string;
    einzelpreis_netto: string; geplant: number; erbracht: number; offen: number;
    /** Über den Plan hinaus erbracht, aber nicht berechnet (Jahresabrechnung). */
    ueber_plan?: number;
    betrag_netto: string;
  }[];
  raten: { id: string; periode: string; soll_netto: string; status: string; rechnung_nummer: string }[];
  perioden_zu_stornieren: { id: string; periode: string; soll_netto: string; status: string }[];
  /** Mehraufwand- und Schlussrechnungen — sie zählen NICHT als berechnete Rate. */
  sonstige_rechnungen: { id: string; periode: string; soll_netto: string; status: string }[];
  /** Gesetzt, sobald die Schlussabrechnung festgeschrieben wurde. */
  festgeschrieben: {
    id: string; periode: string; soll_netto: string; soll_brutto?: string | null;
    faellig_am?: string | null; status: string;
    rechnung_nummer?: string | null; versendet_am?: string | null;
  } | null;
  mehraufwand_offen_netto: string;
  mehraufwand_posten: number;
  mehraufwand: Mehraufwand[];
  abrechnung_id?: string;
  periode?: string;
};

export function useMehraufwand(id: string | undefined) {
  return useQuery<Mehraufwand[]>({
    queryKey: ["pflege", "mehraufwand", id],
    queryFn: () => apiGet<Mehraufwand[]>(`${BASE}/${id}/mehraufwand`),
    enabled: !!id,
  });
}

/** Nach jeder Änderung am Mehraufwand ist auch die Schlussabrechnung veraltet. */
function useMehraufwandSchreiben<T>(id: string | undefined, fn: (v: T) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pflege", "mehraufwand", id] });
      qc.invalidateQueries({ queryKey: ["pflege", "schluss", id] });
      qc.invalidateQueries({ queryKey: ["pflege", "vertrag", id] });
    },
  });
}

export function useMehraufwandAnlegen(id: string | undefined) {
  return useMehraufwandSchreiben<MehraufwandBody>(
    id, body => apiPost<Mehraufwand[]>(`${BASE}/${id}/mehraufwand`, body));
}

export function useMehraufwandLoeschen(id: string | undefined) {
  return useMehraufwandSchreiben<string>(
    id, mid => apiDelete<Mehraufwand[]>(`${BASE}/mehraufwand/${mid}`));
}

export function useMehraufwandAendern(id: string | undefined) {
  return useMehraufwandSchreiben<{ mid: string; body: Partial<MehraufwandBody> }>(
    id, ({ mid, body }) => apiPatch<Mehraufwand[]>(`${BASE}/mehraufwand/${mid}`, body));
}

/** Offene Posten zu EINER eigenen Rechnung bündeln — getrennt vom Zahlrhythmus. */
export function useMehraufwandAbrechnen(id: string | undefined) {
  return useMehraufwandSchreiben<void>(
    id, () => apiPost(`${BASE}/${id}/mehraufwand/abrechnen`, {}));
}

export function useSchlussabrechnung(id: string | undefined, aktiv = true) {
  return useQuery<Schlussabrechnung>({
    queryKey: ["pflege", "schluss", id],
    queryFn: () => apiGet<Schlussabrechnung>(`${BASE}/${id}/schlussabrechnung`),
    enabled: !!id && aktiv,
  });
}

/** Jahresabrechnung (Regelfall): erbrachte Einsätze des Jahres gegen die Raten. */
export function useJahresabrechnung(id: string | undefined, jahr: number) {
  return useQuery<Schlussabrechnung>({
    queryKey: ["pflege", "schluss", id, "jahr", jahr],
    queryFn: () => apiGet<Schlussabrechnung>(`${BASE}/${id}/jahresabschluss?jahr=${jahr}`),
    enabled: !!id,
  });
}

export function useJahresabrechnungFestschreiben(id: string | undefined, jahr: number) {
  return useMehraufwandSchreiben<void>(
    id, () => apiPost<Schlussabrechnung>(`${BASE}/${id}/jahresabschluss?jahr=${jahr}`, {}));
}

/** Die festgeschriebene Jahresabrechnung an den Kunden — erst damit bekommt
 *  eine Nachzahlung ihren Einzugstag (die Abrechnung ist die Ankündigung). */
export function useJahresabrechnungSenden(id: string | undefined, jahr: number) {
  return useMehraufwandSchreiben<void>(
    id, () => apiPost<Schlussabrechnung>(`${BASE}/${id}/jahresabschluss/senden?jahr=${jahr}`, {}));
}

export function jahresabrechnungPdf(id: string, jahr: number) {
  return `${BASE}/${id}/jahresabschluss/pdf?jahr=${jahr}`;
}

export function useSchlussabrechnungFestschreiben(id: string | undefined) {
  return useMehraufwandSchreiben<void>(
    id, () => apiPost<Schlussabrechnung>(`${BASE}/${id}/schlussabrechnung`, {}));
}

// ─────────────────────── Nachträge und Preisrunde (E3) ─────────────────────

export type PreisrundeZeile = {
  id: string; nummer: string; kunde_name: string; kunde_email: string;
  vertragsart: string;
  wirksam_zum: string;
  vorlauf_tage: number;
  /** § 8 verlangt acht Wochen Vorlauf — darunter geht die Ankündigung nicht raus. */
  vorlauf_reicht: boolean;
  jahres_netto_alt: string;
  jahres_netto_neu: string;
  differenz_netto: string;
  /** Was nach der Rundung auf zwei Stellen WIRKLICH herauskommt. */
  prozent_tatsaechlich: string;
  sonderkuendigung: boolean;
  bereits_angekuendigt: string | null;
};

export type Preisrunde = {
  stichtag: string;
  prozent_gewuenscht: string;
  vertraege: PreisrundeZeile[];
  summe_alt: string; summe_neu: string; summe_mehr: string;
  mit_sonderkuendigung: number;
  ohne_vorlauf: number;
};

export function usePreisrunde(prozent: number, aktiv = true) {
  return useQuery<Preisrunde>({
    queryKey: ["pflege", "preisrunde", prozent],
    queryFn: () => apiGet<Preisrunde>(`${BASE}/preisrunde?prozent=${prozent}`),
    enabled: aktiv,
    staleTime: 30_000,
  });
}

export function usePreisrundeAnkuendigen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      vertraege: { id: string; prozent: number; wirksam_zum: string }[];
      betreff?: string; text?: string;
    }) => apiPost<{ ok: boolean; angekuendigt: unknown[]; fehler: unknown[] }>(
      `${BASE}/preisrunde/ankuendigen`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pflege", "preisrunde"] });
      qc.invalidateQueries({ queryKey: ["pflege", "vertraege"] });
      qc.invalidateQueries({ queryKey: ["pflege", "fristen"] });
    },
  });
}

/** Nachtrag anlegen — neue Version als Entwurf, Positionen kopiert.
 *  `prozent` hebt dabei alle Einzelpreise an (angekündigte Preisanpassung). */
export function useNachtrag(id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { prozent?: number; beginn?: string | null }) =>
      apiPost<VertragDetail>(`${BASE}/${id}/nachtrag`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pflege"] }),
  });
}

/** Datum aus dem Backend (ISO) als deutsches Datum. Leere Werte bleiben leer,
 *  statt „Invalid Date" in die Oberfläche zu schreiben. */
export function datum(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("de-DE");
}

export const MONAT_KURZ = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

// ─────────────────────────── Wizard (W8) ───────────────────────────────────
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { KatalogEintrag, KundeObjekt, KundeLite, VertragPosition } from "../lib/wizard";

/** Leistungskatalog. `alle` holt auch stillgelegte Einträge — das braucht nur
 *  der Katalog-Bereich; der Wizard soll Stillgelegtes nicht vorschlagen. */
export function useKatalog(alle = false) {
  return useQuery<KatalogEintrag[]>({
    queryKey: ["pflege", "katalog", alle],
    queryFn: () => apiGet<KatalogEintrag[]>(`${BASE}/katalog${alle ? "?alle=1" : ""}`),
    staleTime: 5 * 60_000,
  });
}

export type KatalogPatch = Partial<{
  code: string; titel: string; beschreibung: string; kategorie: string; einheit: string;
  vk_netto: number; kosten_netto: number; zeit_min_je_einheit: number;
  lohnanteil_prozent: number; standard_monate: number[]; garten_typen: string[];
  vogelschutz_relevant: boolean; kalibriert: boolean; aktiv: boolean; sort_order: number;
  max_je_monat: number;
}>;

/** Anlegen und Ändern teilen sich die Invalidierung: nach jedem Schreiben ist
 *  jede Katalogsicht veraltet — auch die des Wizards im anderen Tab. */
function useKatalogSchreiben<T>(fn: (v: T) => Promise<KatalogEintrag>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pflege", "katalog"] }),
  });
}

export function useKatalogAnlegen() {
  return useKatalogSchreiben<KatalogPatch>(body => apiPost<KatalogEintrag>(`${BASE}/katalog`, body));
}

export function useKatalogAendern() {
  return useKatalogSchreiben<{ id: string; body: KatalogPatch }>(
    ({ id, body }) => apiPatch<KatalogEintrag>(`${BASE}/katalog/${id}`, body),
  );
}

/**
 * Kundenauswahl im Wizard. Gesucht wird auf dem Server: die Liste ohne
 * Suchbegriff ist gekappt, und bis 07.10.2026 filterte der Wizard nur diese
 * gekappte Liste — alle Kunden nach „G…" waren nicht wählbar.
 */
export function useKundenListe(suche = "") {
  const q = suche.trim();
  return useQuery<KundeLite[]>({
    queryKey: ["pflege", "kunden", q],
    queryFn: () => apiGet<KundeLite[]>(`${BASE}/kunden${q ? `?suche=${encodeURIComponent(q)}` : ""}`),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

/** Ein Kunde einzeln — für einen Wizard, der über `?kunde=` geöffnet wurde. */
export function useKunde(kundeId: string | undefined) {
  return useQuery<KundeLite | null>({
    queryKey: ["pflege", "kunde", kundeId],
    queryFn: () => apiGet<KundeLite[]>(`${BASE}/kunden?id=${encodeURIComponent(kundeId || "")}`)
      .then(l => l[0] ?? null),
    enabled: !!kundeId,
    staleTime: 60_000,
  });
}

export function useKundeObjekt(kundeId: string | undefined) {
  return useQuery<KundeObjekt>({
    queryKey: ["pflege", "objekt", kundeId],
    queryFn: () => apiGet<KundeObjekt>(`${BASE}/kunde/${kundeId}/objekt`),
    enabled: !!kundeId,
  });
}

export function useUpsertKundeObjekt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ kundeId, body }: { kundeId: string; body: Record<string, unknown> }) =>
      apiPost(`${BASE}/kunde/${kundeId}/objekt`, body),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ["pflege", "objekt", v.kundeId] }),
  });
}

export type VertragCreateBody = {
  kunde_id: string;
  vertragsart: string;
  beginn?: string | null;
  laufzeit_monate: number;
  verlaengerung_monate: number;
  kuendigungsfrist_monate: number;
  zahlweise: string;
  zahlungsart: string;
  faktor: number;
  sondervereinbarungen: string;
  leistungen: VertragPosition[];
};

/** Entwurf im Hintergrund fortschreiben. Der Server ersetzt bei `leistungen`
 *  immer die ganze Positionsliste und rechnet die Summen neu — deshalb wird
 *  hier der komplette Stand geschickt, nicht ein Teilstueck. */
export function usePatchVertrag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<VertragCreateBody> }) =>
      apiPatch<VertragDetail>(`${BASE}/${id}`, body),
    // Bewusst KEIN invalidateQueries auf den Vertrag selbst: ein Refetch
    // mitten im Tippen wuerde die Eingabe ueberschreiben. Die Liste darf sich
    // aktualisieren, die offene Maske nicht.
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pflege", "vertraege"] }),
  });
}

/** Entwurf verwerfen. Der Server laesst das nur im Status „entwurf“ zu —
 *  alles danach hat Rechtswirkung. */
export function useVertragVerwerfen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete<{ ok: boolean }>(`${BASE}/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pflege"] }),
  });
}

export function useCreateVertrag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: VertragCreateBody) => apiPost<VertragDetail>(BASE, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pflege"] }),
  });
}

// ── Vertragsabschluss (W9): Zustimmungs-Link, Mail, Aktivierung ─────────────

/** Zustimmungs-Link erstellen (Entwurf → angeboten). Idempotent im Backend. */
/**
 * Ein Kundenlink zum Durchklicken, der nichts auslöst.
 *
 * Anders als `useZustimmungErstellen`: keine Vertragsnummer wird vergeben, die
 * Textfassung nicht festgeschrieben, der Status bleibt stehen. Zustimmen ist
 * auf der Kundenseite gesperrt. Gedacht zum Prüfen des Ablaufs, bevor der
 * echte Link herausgeht.
 */
export function useProbelauf(id: string | undefined) {
  return useMutation({
    mutationFn: () =>
      apiPost<{ token: string; url: string; probelauf: boolean }>(
        `${BASE}/${id}/probelauf`, {}),
  });
}

export function useZustimmungErstellen(id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<ZustimmungInfo>(`${BASE}/${id}/zustimmung`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pflege", "vertrag", id] });
      qc.invalidateQueries({ queryKey: ["pflege", "vertraege"] });
    },
  });
}

/** Die Angebotsmail, wie sie rausginge — Empfänger, Betreff, Text. Erst auf
 *  Anforderung laden (`enabled`), sonst fragt jede Detailseite danach. */
export function useAngebotsmailVorschau(id: string | undefined, enabled: boolean) {
  return useQuery<{ an: string; betreff: string; text: string }>({
    queryKey: ["pflege", "angebotsmail", id],
    queryFn: () => apiGet(`${BASE}/${id}/zustimmung-mail`),
    enabled: !!id && enabled,
    staleTime: 0,
  });
}

/** Zustimmungs-Link per E-Mail an den Kunden senden. */
export function useZustimmungMail(id: string | undefined) {
  return useMutation({
    mutationFn: () => apiPost<{ ok: boolean; gesendet_an: string }>(`${BASE}/${id}/zustimmung-mail`, {}),
  });
}

/** Zugestimmten Vertrag aktivieren (Projekt + Jahres-Einsatzplan). */
export function useAktivieren(id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<{ ok?: boolean; termine?: number } & Record<string, unknown>>(`${BASE}/${id}/aktivieren`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pflege", "vertrag", id] });
      qc.invalidateQueries({ queryKey: ["pflege", "vertraege"] });
    },
  });
}

// ─────────────────────── Einsatzakte und Leistungsnachweis (E4) ────────────

/** Die fünf Zustände aus § 5 des Vertrages. Die Wirkung aufs Geld steht im
 *  Backend (`vertrag_einsatz.py`) — hier nur, wie sie heißen. */
export const EINSATZ_STATUS = [
  { wert: "durchgefuehrt", label: "durchgeführt" },
  { wert: "zugang_verwehrt", label: "Zugang verwehrt" },
  { wert: "verschoben", label: "verschoben" },
  { wert: "entfallen", label: "fachlich entfallen" },
  { wert: "geplant", label: "geplant" },
] as const;

export type EinsatzPosition = {
  id: string;
  einsatz_id: string;
  leistung_id: string;
  erledigt: boolean;
  menge: string;
  notiz: string;
  titel: string;
};

export type EinsatzFoto = {
  id: string;
  einsatz_id: string;
  art: "vorher" | "nachher" | "doku";
  dateiname: string;
  mime: string;
  groesse: number;
  nextcloud_pfad: string;
  notiz: string;
  created_at: string;
};

export type Einsatz = {
  id: string;
  vertrag_id: string;
  termin_id: string | null;
  datum: string;
  status: string;
  angekuendigt_am: string | null;
  durchgefuehrt_am: string | null;
  beginn_zeit: string | null;
  ende_zeit: string | null;
  dauer_min: string | null;
  /** Abzug bei verwehrtem Zugang — § 5 verlangt ihn, sonst ist die Klausel angreifbar. */
  ersparte_aufwendungen: string;
  wetter: string;
  notiz: string;
  mitarbeiter: string;
  positionen: EinsatzPosition[];
  fotos: EinsatzFoto[];
};

export type EinsatzFortschritt = {
  leistung_id: string; titel: string;
  soll: number; ist: number; entfallen: number; offen: number;
};

export type Einsatzakte = {
  vertrag_id: string;
  nummer: string | null;
  kunde_name: string | null;
  einsaetze: Einsatz[];
  /** Geplante Termine, zu denen noch keine Akte existiert. */
  offene_termine: { id: string; start_at: string; end_at: string; title: string;
                    abgesagt_am: string | null; leistung_ids: string[] }[];
  fortschritt: EinsatzFortschritt[];
};

export type EinsatzBody = {
  termin_id?: string | null;
  datum?: string;
  status?: string;
  angekuendigt_am?: string | null;
  durchgefuehrt_am?: string | null;
  beginn_zeit?: string | null;
  ende_zeit?: string | null;
  dauer_min?: number;
  ersparte_aufwendungen?: number;
  wetter?: string;
  notiz?: string;
  mitarbeiter?: string;
  positionen?: string[];
};

export function useEinsaetze(id: string | undefined) {
  return useQuery<Einsatzakte>({
    queryKey: ["pflege", "einsaetze", id],
    queryFn: () => apiGet<Einsatzakte>(`${BASE}/${id}/einsaetze`),
    enabled: !!id,
  });
}

/** Eine Änderung an der Akte verändert auch die Schlussabrechnung — sie liest
 *  jetzt aus derselben Quelle. Beide Abfragen zusammen zu verwerfen ist
 *  billiger als eine Zahl, die zwei Ansichten unterschiedlich anzeigen. */
function useEinsatzSchreiben<T>(id: string | undefined, fn: (v: T) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pflege", "einsaetze", id] });
      qc.invalidateQueries({ queryKey: ["pflege", "schluss", id] });
    },
  });
}

export function useEinsatzAnlegen(id: string | undefined) {
  return useEinsatzSchreiben<EinsatzBody>(
    id, body => apiPost<Einsatz>(`${BASE}/${id}/einsaetze`, body));
}

export function useEinsaetzeAusTerminen(id: string | undefined) {
  return useEinsatzSchreiben<void>(
    id, () => apiPost(`${BASE}/${id}/einsaetze/aus-terminen`, {}));
}

export function useEinsatzAendern(id: string | undefined) {
  return useEinsatzSchreiben<{ eid: string; body: EinsatzBody }>(
    id, ({ eid, body }) => apiPatch<Einsatz>(`${BASE}/einsaetze/${eid}`, body));
}

export function useEinsatzLoeschen(id: string | undefined) {
  return useEinsatzSchreiben<string>(
    id, eid => apiDelete(`${BASE}/einsaetze/${eid}`));
}

export function useEinsatzFotoLoeschen(id: string | undefined) {
  return useEinsatzSchreiben<{ eid: string; fid: string }>(
    id, ({ eid, fid }) => apiDelete(`${BASE}/einsaetze/${eid}/foto/${fid}`));
}

/** Bild-Upload läuft an apiPost vorbei: FormData verträgt keinen JSON-Header. */
export function useEinsatzFoto(id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ eid, art, datei }: { eid: string; art: string; datei: File }) => {
      const daten = new FormData();
      daten.append("datei", datei);
      const r = await fetch(`${BASE}/einsaetze/${eid}/foto?art=${encodeURIComponent(art)}`, {
        method: "POST", body: daten, credentials: "include",
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Upload fehlgeschlagen");
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pflege", "einsaetze", id] }),
  });
}

export function fotoUrl(eid: string, fid: string) {
  return `${BASE}/einsaetze/${eid}/foto/${fid}`;
}

/** Ohne Jahr das laufende Vertragsjahr, mit Jahr das Kalenderjahr — so fragt
 *  das Finanzamt. */
export function nachweisUrl(id: string, jahr?: number) {
  return `${BASE}/${id}/leistungsnachweis` + (jahr ? `?jahr=${jahr}` : "");
}

/** § 6: ab 75 € netto braucht eine Zusatzleistung Textform. Der Link holt sie. */
export function useFreigabeLink(id: string | undefined) {
  return useMehraufwandSchreiben<string>(
    id, mid => apiPost<{ link: string }>(`${BASE}/mehraufwand/${mid}/freigabe-link`, {}));
}

export type Nachzutragen = {
  tage: number;
  termine_offen: number;
  vertraege: { id: string; nummer: string; status: string; kunde_name: string | null;
               offen: number; aeltester: string }[];
};

/** Wo ist gefahren worden, ohne dass etwas in der Akte steht? */
export function useEinsaetzeNachzutragen(tage = 120) {
  return useQuery<Nachzutragen>({
    queryKey: ["pflege", "einsaetze-offen", tage],
    queryFn: () => apiGet<Nachzutragen>(`${BASE}/einsaetze/nachzutragen?tage=${tage}`),
  });
}

// ─────────────────────── Bestand und Kundenakte (E5) ───────────────────────

export type BestandZeile = {
  id: string; nummer: string; kunde_name: string | null;
  vertragsart: string; beginn: string | null; preisanpassung_zum: string | null;
  jahres_netto: string; jahres_kosten: string; jahres_zeit_stunden: string;
  deckungsbeitrag: string; db_prozent: string | null; db_pro_stunde: string | null;
  /** gruen | gelb | rot | unbekannt — „unbekannt" heißt: keine Zeiten hinterlegt. */
  ampel: "gruen" | "gelb" | "rot" | "unbekannt";
};

export type Bestand = {
  vertraege: BestandZeile[];
  anzahl: number;
  jahres_netto: string; jahres_kosten: string; deckungsbeitrag: string;
  db_prozent: string | null;
  jahres_stunden: string; db_pro_stunde: string | null;
  /** Messlatte der Ampel. `referenz_art` sagt, woher sie kommt. */
  referenz_db_stunde: string;
  referenz_art: "ziel" | "median";
  rot: number; gelb: number; unbekannt: number;
  auslastung: { monat: number; kurz: string; stunden: string; einsaetze: number }[];
};

export function useBestand(zielDbStunde = 0) {
  return useQuery<Bestand>({
    queryKey: ["pflege", "bestand", zielDbStunde],
    queryFn: () => apiGet<Bestand>(`${BASE}/bestand?ziel_db_stunde=${zielDbStunde}`),
  });
}

export type KundenakteVertrag = {
  id: string; nummer: string; status: string;
  beginn: string | null; ende: string | null; gekuendigt_zum: string | null;
  laufzeit_monate: number; zahlweise: string; zahlungsart: string; vertragsart: string;
  jahres_netto: string | null; monats_netto: string | null;
  version: number; vorgaenger_id: string | null; preisanpassung_zum: string | null;
  ausfertigung: { token: string | null; erstellt_am: string; dateiname: string;
                  sha256: string } | null;
  einsaetze: { datum: string; status: string; leistungen: string }[];
};

export type Kundenakte = {
  kunde_id: string;
  kunde: { id: string; name: string; email: string };
  vertraege: KundenakteVertrag[];
  /** Der dauerhafte Lesezugang — null, solange keiner vergeben wurde. */
  portal: { token: string; link: string; erstellt_am: string;
            letzter_zugriff: string | null; zugriffe: number } | null;
};

export function useKundenakte(kundeId: string | undefined) {
  return useQuery<Kundenakte>({
    queryKey: ["pflege", "kundenakte", kundeId],
    queryFn: () => apiGet<Kundenakte>(`${BASE}/kunde/${kundeId}/akte`),
    enabled: !!kundeId,
  });
}

function usePortalSchreiben<T>(kundeId: string | undefined, fn: (v: T) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pflege", "kundenakte", kundeId] }),
  });
}

/** Ein zweiter Aufruf widerruft den alten Link und gibt einen neuen — er ist
 *  damit zugleich die Notbremse, wenn ein Link in falsche Hände geraten ist. */
export function useKundenportalErstellen(kundeId: string | undefined) {
  return usePortalSchreiben<void>(
    kundeId, () => apiPost(`${BASE}/kunde/${kundeId}/portal`, {}));
}

/** Der eine Kundenlink zum Kopieren: vorhandenen holen oder anlegen. Anders
 *  als `useKundenportalErstellen` macht das keinen bestehenden Link ungültig. */
export function useKundenlinkSichern(kundeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<{ link: string }>(`${BASE}/kunde/${kundeId}/kundenlink`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pflege", "kundenakte", kundeId] }),
  });
}

export function useKundenportalWiderrufen(kundeId: string | undefined) {
  return usePortalSchreiben<void>(
    kundeId, () => apiDelete(`${BASE}/kunde/${kundeId}/portal`));
}
